// **الصفحة الرئيسية** (قرار المالك 22/09/2026: «عاوز زرار أول ما يفتح على الموبايل تظهر
// واسمها الصفحة الرئيسية»، ثم «اعملها WEB وعلى الأبليكيشن»).
//
// وهي لا تكرّر شاشةً أخرى: كل ما فيها يجيب عن سؤالين لا ثالث لهما —
//   **«أعمل إيه دلوقتي؟»**  و  **«أنا ماشي إزاي؟»**
// فما لا يجيب عن أحدهما فليس مكانه هنا، مهما كان مفيدًا في شاشته.
//
// **ولا رقمَ يُحسب هنا**: كل عددٍ معروض يأتي من المسار الذي تقرؤه شاشتُه الأصلية —
// `/kpi` و`/me/crm/summary` و`/me/lead-bank` و`/bookings/upcoming` و`/admin/crm/summary`
// و`/admin/dashboard`. فلو تغيّرت قاعدةُ حسابٍ في الخادم تغيّر ما يُقرأ هنا معها، ولا
// يبقى رقمان لشيءٍ واحد يختلفان.
//
// **وفشلُ بابٍ لا يُسقط الصفحة**: النداءات تُطلب معًا بـ`allSettled`، وما تعذّر منها
// يُخفى صندوقُه وحده — فمن لا يُسمح له بلوحة الأدمن يرى بقيّة صفحته كاملة.
//
// والشكل واحدٌ على الويب وعلى الهاتف: شبكةٌ تنزل عمودًا واحدًا على الضيّق وعمودين على
// الواسع. لا نسخةٌ ثانية تُصان مرّتين.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  get, qs,
  type AdminDashboard, type AdminDaysResponse, type AdminSummary, type KpiReport, type Me, type SellerSummary,
} from '../lib/api.ts';
import { Card, Empty, Spinner } from '../components/ui.tsx';
import { requestOpen } from '../lib/openIntent.ts';
import { cairoParts, cairoTimeAr, cairoToday, dayPeriodAr, egpNum, EGP, monthTitle, shortDate } from '../lib/format.ts';
import { greetAr } from '../lib/greeting.ts';
import { GreetingIcon } from './GreetingIcon.tsx';

/** رصيد بنك الأرقام كما يعيده `/me/lead-bank` — نفس ما تقرؤه بطاقةُ البنك في «متابعاتي». */
interface LeadBankBrief { total: number; available: number; pulledToday: number; limit: number; remainingToday: number }
/** رحلةٌ قادمة كما يعيدها `/bookings/upcoming` — ما يلزم للسطر وحده. */
interface TripBrief { id: string; bookingNo: string; customerName: string; toCity: string | null; departureDate: string }
/** إحصاء طابور مراجعة المدفوعات — العدد وحده يكفي هنا. */
interface ReviewBrief { stats: { pendingCount: number } }

/** المال بالجنيه الصحيح — والقاعدة واحدةٌ في البرنامج كلّه الآن (`egpNum`). */
const whole = (v: string | number) => `${egpNum(v)} ${EGP}`;

const TONES = {
  plain: 'border-slate-200 bg-white text-ink-900',
  good: 'border-emerald-200 bg-emerald-50/60 text-emerald-900',
  warn: 'border-amber-200 bg-amber-50/60 text-amber-900',
  bad: 'border-rose-200 bg-rose-50/60 text-rose-900',
} as const;
type Tone = keyof typeof TONES;

/**
 * خانةُ رقم: الرقم أوّلًا وكبيرًا، واسمُه تحته. وإن كان لها وجهةٌ صارت زرًّا —
 * فالرقم الذي يُقرأ ولا يُفتح نصفُ فائدة.
 */
function Stat({ n, label, tone = 'plain', hint, onClick }: {
  n: number | string; label: string; tone?: Tone; hint?: string; onClick?: () => void;
}) {
  const body = (
    <>
      <div className="text-2xl font-bold tabular-nums leading-none">{typeof n === 'number' ? n.toLocaleString('en') : n}</div>
      <div className="mt-1.5 text-xs font-medium leading-snug opacity-80">{label}</div>
      {hint && <div className="mt-0.5 text-[0.9em] text-xs opacity-60">{hint}</div>}
    </>
  );
  const cls = `rounded-xl border p-3 text-start ${TONES[tone]}`;
  return onClick
    ? <button type="button" onClick={onClick} className={`${cls} transition-transform active:scale-[.97]`}>{body}</button>
    : <div className={cls}>{body}</div>;
}

/** صفٌّ من الخانات — عمودان على الهاتف وأربعة على الواسع. */
const Grid = ({ children }: { children: ReactNode }) =>
  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">{children}</div>;

/**
 * **الأدوات الأربع في «على طول»** (قرار المالك 22/09/2026): الأكثرُ فتحًا في اليوم —
 * فندقٌ مبدئي، وحاسبة سعر البيع، ومحوّل العملات، والدليل. موضعُها في عمود التنقّل
 * باقٍ كما هو؛ هذه طريقٌ ثانٍ أقصر لا بديلٌ عنه.
 */
const QUICK_TOOLS: readonly { id: string; label: string }[] = [
  { id: 'hotel-voucher', label: 'حجز فندق مبدئي' },
  { id: 'pricing', label: 'حاسبة سعر البيع' },
  { id: 'fx', label: 'محوّل العملات' },
  { id: 'guide', label: 'دليلك في السياحة' },
];

/**
 * زرُّ وجهة — «افتح الشاشة الفلانية».
 *
 * و`primary` للفعلين اللذين تذهب إليهما اليدُ قبل غيرهما (قرار المالك 22/09/2026:
 * «وادّي لون لدول ومَيِّزهم علشان دول أكتر حاجة الإيد هتروح عليهم»): حجزٌ جديد
 * ومتابعةٌ جديدة. والتمييزُ بالأرضية والوزن معًا لا باللون وحده.
 */
const Go = ({ children, onClick, primary = false }: {
  children: ReactNode; onClick: () => void; primary?: boolean;
}) => (
  <button type="button" onClick={onClick}
    className={`rounded-lg px-3 py-1.5 text-sm transition-all duration-150 active:scale-[0.98] ${
      primary
        ? 'bg-brand-600 font-bold text-white shadow-sm shadow-brand-600/30 hover:bg-brand-700'
        : 'border border-slate-300 bg-white font-semibold text-ink-700 hover:border-brand-400 hover:text-brand-700'}`}>
    {children}
  </button>
);

/**
 * شريطُ التارجت: المحقَّق من المطلوب. ولا يُعرض إن لم يكن للشهر تارجتٌ مضبوط —
 * شريطٌ بلا سقفٍ لا يقول شيئًا.
 */
function TargetBar({ done, target }: { done: number; target: number }) {
  const pct = Math.min(100, Math.round((done / target) * 100));
  const left = Math.max(0, target - done);
  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-sm text-ink-700">
          حجوزات <b>New</b> الشهر ده: <b className="tabular-nums text-ink-900">{done}</b> من <b className="tabular-nums">{target}</b>
        </div>
        <div className="text-sm font-bold tabular-nums text-brand-700">{pct}٪</div>
      </div>
      <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full transition-[width] duration-500 ${pct >= 100 ? 'bg-emerald-600' : 'bg-brand-600'}`}
          style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1.5 text-xs text-ink-500">
        {left === 0 ? 'وصلت التارجت — اللي بعد كده بيرفع شريحتك.' : `فاضل ${left} حجز New للتارجت.`}
      </div>
    </div>
  );
}

export function HomePage({ me, pendingLeaves, onGo }: {
  me: Me;
  /** طلبات الإجازة المعلّقة — محسوبةٌ في `App` أصلًا، فلا تُطلب مرّتين */
  pendingLeaves: number;
  onGo: (tab: string) => void;
}) {
  const admin = me.role === 'ADMIN';
  const today = useMemo(() => cairoToday(), []);
  const month = useMemo(() => today.slice(0, 7), [today]);

  const [kpi, setKpi] = useState<KpiReport | null>(null);
  const [mine, setMine] = useState<SellerSummary | null>(null);
  const [bank, setBank] = useState<LeadBankBrief | null>(null);
  const [trips, setTrips] = useState<TripBrief[] | null>(null);
  const [crm, setCrm] = useState<AdminSummary | null>(null);
  /**
   * **تواصلُ اليوم وحده** (قرار المالك 02/10/2026: «ليه متابعات الشركة متسجّلة 142 تواصل اليوم؟ … مش المفروض ده متابعات اليوم
   * بيوم؟»): الملخّص بلا تاريخ يعدّ كل ما سُجّل منذ البداية — فكان «تواصل النهاردة» و«اتحوّل لحجز» مجموعَ كل الأيام. فيُطلب
   * ملخّصُ اليوم وحده لهما، ويبقى المستحقّ والمتأخّر من الملخّص الكامل (موعدُهما اليوم أو قبله أيًّا كان يوم تسجيلهما).
   */
  const [crmToday, setCrmToday] = useState<AdminSummary | null>(null);
  const [dash, setDash] = useState<AdminDashboard | null>(null);
  const [review, setReview] = useState<number | null>(null);
  const [att, setAtt] = useState<AdminDaysResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    /** كل بابٍ يُفتح وحده: ما تعذّر منه يُخفى صندوقُه ولا يُسقط الصفحة. */
    const ask = <T,>(path: string, set: (v: T) => void, ok: (v: T) => boolean = () => true) =>
      get<T>(path)
        // ردٌّ ناقصُ الشكل لا يُعرض نصفَه: الصندوق يُخفى كما لو تعذّر النداء. وهي قاعدةُ
        // الشاشات القائمة نفسها (انظر CrmPage) — صفحةٌ ناقصةٌ خيرٌ من صفحةٍ مكسورة.
        .then(v => { if (v && ok(v)) set(v); })
        .catch(() => { /* صندوقٌ لا يُعرض خيرٌ من صفحةٍ لا تُفتح */ });

    const jobs = [
      ask<KpiReport>(`/kpi${qs({ month })}`, setKpi, r => Array.isArray(r.rows)),
      ...(admin ? [
        ask<AdminSummary>('/admin/crm/summary', setCrm, r => Boolean(r.totals)),
        ask<AdminSummary>(`/admin/crm/summary${qs({ from: today, to: today })}`, setCrmToday, r => Boolean(r.totals)),
        ask<AdminDashboard>(`/admin/dashboard${qs({ month })}`, setDash, r => Boolean(r.totals) && Array.isArray(r.notCalculated)),
        ask<ReviewBrief>(`/admin/payments/review${qs({ limit: 1 })}`, r => setReview(r.stats.pendingCount), r => Boolean(r.stats)),
        // لوحةُ اليوم نفسها التي تقرؤها شاشة «الحضور والإجازات» — يومٌ واحد لا أكثر
        ask<AdminDaysResponse>(`/admin/attendance/days${qs({ from: today, to: today, limit: 200 })}`,
          setAtt, r => Array.isArray(r.items)),
      ] : [
        ask<SellerSummary>('/me/crm/summary', setMine, r => Boolean(r.today && r.dueFollowUps && r.totals)),
        ask<LeadBankBrief>('/me/lead-bank', setBank, r => typeof r.available === 'number'),
      ]),
      ask<{ items: TripBrief[] }>(`/bookings/upcoming${qs({ days: 7 })}`, r => setTrips(r.items), r => Array.isArray(r.items)),
    ];
    await Promise.allSettled(jobs);
    setLoading(false);
  }, [admin, month, today]);
  useEffect(() => { void load(); }, [load]);

  const mineRow = kpi?.rows.find(r => r.userId === me.id) ?? (kpi?.scope === 'SELF' ? kpi.rows[0] : undefined);
  const target = kpi?.newTarget ?? mineRow?.newTarget ?? null;

  /**
   * **«على طول» تحت مهامّ اليوم** (قرار المالك 22/09/2026: «خلّي على طول تحت مهام
   * النهاردة»): أوّلُ ما يُقرأ ما ينتظرك، ثمّ ما تفعله. وهي تُعرَّف مرّةً وتُوضع في
   * موضعها من كل دور — فلا تُكتب مرّتين.
   */
  const quick = (
    <Card title="⚡ على طول">
      <div className="flex flex-wrap gap-2">
        {admin ? (
          <>
            <Go primary onClick={() => { requestOpen('bookings', 'new'); onGo('bookings'); }}>
              حجز جديد [ ريكويست جديد ]
            </Go>
            {/* ومتابعةٌ جديدة بجانبه وبنفس وضوحه (قرار المالك 22/09/2026): «حط كمان بعدها
                متابعة جديدة وخلّيها واضحة زيّ حجز جديد» — والأدمن يسجّل متابعاته كالبياع. */}
            <Go primary onClick={() => { requestOpen('crm', 'new'); onGo('crm'); }}>متابعة جديدة</Go>
            <Go onClick={() => onGo('admin-dashboard')}>تقارير</Go>
            <Go onClick={() => onGo('admin-crm')}>نشاط الشركة</Go>
            <Go onClick={() => onGo('admin-settlements')}>الإدارة المالية</Go>
            {QUICK_TOOLS.map(t => <Go key={t.id} onClick={() => onGo(t.id)}>{t.label}</Go>)}
          </>
        ) : (
          <>
            {/* بالاسم نفسه الذي في شاشة الحجوزات — زرٌّ واحدٌ لا اسمان لفعلٍ واحد */}
            <Go primary onClick={() => { requestOpen('bookings', 'new'); onGo('bookings'); }}>
              حجز جديد [ ريكويست جديد ]
            </Go>
            <Go primary onClick={() => { requestOpen('crm', 'new'); onGo('crm'); }}>تسجيل متابعة جديدة</Go>
            <Go onClick={() => onGo('quote')}>عرض سعر</Go>
            <Go onClick={() => onGo('commission')}>عمولتي</Go>
            {QUICK_TOOLS.map(t => <Go key={t.id} onClick={() => onGo(t.id)}>{t.label}</Go>)}
          </>
        )}
      </div>
    </Card>
  );

  if (loading && kpi === null) return <Spinner label="جارٍ تجهيز صفحتك…" />;

  return (
    <div className="space-y-4">
      {/* شمسٌ تضحك في الصبح وقمرٌ نائم بالليل (قرار المالك 22/09/2026) — بجانب التحيّة لا فوقها */}
      <header className="flex items-center gap-3 rounded-xl border border-brand-100 bg-gradient-to-l from-brand-50/70 to-white p-4">
        <GreetingIcon />
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-ink-900">{greetAr(me.fullNameAr)}</h2>
          <p className="mt-1 text-sm text-ink-600">
            {/* اسم اليوم بالعربي والتاريخ 01/10/2026 بساعة القاهرة — كبقية البرنامج (قرار المالك 02/10/2026) */}
            {new Date().toLocaleDateString('ar-EG', { weekday: 'long', timeZone: 'Africa/Cairo' })} {shortDate(cairoToday())}
            {' · '}{dayPeriodAr(cairoParts(new Date()).hour)}
          </p>
        </div>
      </header>

      {admin ? (
        <>
          {/* مستني قرارك — أوّلُ ما يُسأل عنه الأدمن: في حاجة واقفة عليّ؟ */}
          <Card title="⏳ مستني قرارك">
            {pendingLeaves === 0 && (review ?? 0) === 0 ? (
              <Empty>مفيش حاجة واقفة عليك دلوقتي — تمام.</Empty>
            ) : (
              <Grid>
                {pendingLeaves > 0 && (
                  <Stat n={pendingLeaves} label="طلب إجازة مستني" tone="warn"
                    onClick={() => onGo('admin-attendance')} />
                )}
                {(review ?? 0) > 0 && (
                  <Stat n={review ?? 0} label="دفعة للمراجعة" tone="warn"
                    onClick={() => onGo('admin-payments')} />
                )}
              </Grid>
            )}
          </Card>

          {quick}

          {crm && (
            <Card title="📞 متابعات الشركة النهاردة">
              <Grid>
                {/* المستحقة والمتأخرة تفتح العملاء أنفسهم لا الإحصاءات (قرار المالك 01/10/2026: «لما أضغط على مستحقة
                    اليوم البرنامج يروح للعملاء اللي محتاجين متابعة اليوم بحيث يتعامل معاهم على طول») */}
                <Stat n={crm.totals.dueToday} label="مستحقة النهاردة"
                  tone={crm.totals.dueToday > 0 ? 'warn' : 'plain'} onClick={() => { requestOpen('crm', 'due'); onGo('crm'); }} />
                <Stat n={crm.totals.overdue} label="متأخرة"
                  tone={crm.totals.overdue > 0 ? 'bad' : 'good'} onClick={() => { requestOpen('crm', 'overdue'); onGo('crm'); }} />
                <Stat n={crmToday?.totals.contacts ?? 0} label="تواصل النهاردة" />
                <Stat n={crmToday?.totals.convertedToBooking ?? 0} label="اتحوّل لحجز" tone="good" />
              </Grid>
            </Card>
          )}

          {kpi && (
            <Card title={`📊 الشركة — ${monthTitle(month)}`}>
              <Grid>
                <Stat n={kpi.rows.reduce((t, r) => t + r.day.total, 0)} label="حجوزات النهاردة" />
                <Stat n={kpi.rows.reduce((t, r) => t + r.monthToDate.NEW, 0)} label="New الشهر ده" tone="good" />
                <Stat n={kpi.rows.reduce((t, r) => t + r.monthToDate.total, 0)} label="كل عمليات الشهر" />
                <Stat n={kpi.rows.length} label="بياعين نشطين" onClick={() => onGo('admin-dashboard')} />
              </Grid>
            </Card>
          )}

          {att && att.items.length > 0 && (
            <Card title="🕘 الحضور النهاردة"
              action={<Go onClick={() => onGo('admin-attendance')}>التفاصيل</Go>}>
              <Grid>
                <Stat n={att.items.filter(d => d.checkedIn).length} label="بصموا حضور" tone="good" />
                <Stat n={att.items.filter(d => !d.checkedIn).length} label="لسه ما بصموش"
                  tone={att.items.some(d => !d.checkedIn) ? 'warn' : 'plain'} />
                <Stat n={att.items.filter(d => d.checkedIn && !d.checkedOut).length} label="لسه في الشغل" />
                <Stat n={att.items.filter(d => d.checkedOut).length} label="انصرفوا" />
              </Grid>
              {/* سطرٌ لكل فرد: الاسم ووقتاه — مختصرٌ يُقرأ بنظرة، والتفصيل في شاشته */}
              <ul className="mt-3 grid gap-1.5 sm:grid-cols-2">
                {[...att.items]
                  .sort((a, b) => Number(b.checkedIn) - Number(a.checkedIn)
                    || (a.fullNameAr ?? '').localeCompare(b.fullNameAr ?? '', 'ar'))
                  .map(d => (
                    <li key={d.userId}
                      className="flex items-baseline justify-between gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5">
                      <span className="min-w-0 truncate text-sm font-medium text-ink-900">{d.fullNameAr ?? '—'}</span>
                      {d.checkedIn ? (
                        <span className="shrink-0 text-xs tabular-nums text-ink-600">
                          <span className="text-emerald-700">{cairoTimeAr(d.firstCheckInAt)}</span>
                          {' ← '}
                          {d.checkedOut
                            ? cairoTimeAr(d.lastCheckOutAt)
                            : <span className="text-amber-700">لسه</span>}
                        </span>
                      ) : (
                        <span className="shrink-0 text-xs font-semibold text-rose-700">ما بصمش</span>
                      )}
                    </li>
                  ))}
              </ul>
            </Card>
          )}

          {dash && (
            <Card title="💰 فلوس الشهر">
              <Grid>
                <Stat n={whole(dash.totals.grossProfit)} label="ربح المبيعات" tone="good" />
                <Stat n={whole(dash.totals.finalCommissionTotal)} label="العمولات" />
                <Stat n={whole(dash.totals.bonusAmount)} label="البونص" />
                <Stat n={whole(dash.totals.totalPayable)} label="الإجمالي المستحق" tone="warn"
                  onClick={() => onGo('admin-settlements')} />
              </Grid>
              {dash.notCalculated.length > 0 && (
                <p className="mt-2 text-xs text-amber-800">
                  لسه {dash.notCalculated.length} بياع ما اتحسبتش عمولته الشهر ده.
                </p>
              )}
            </Card>
          )}
        </>
      ) : (
        <>
          {mine && (
            <Card title="✅ مهامّ النهاردة">
              <Grid>
                <Stat n={mine.dueFollowUps.dueToday} label="متابعة مستحقة النهاردة"
                  tone={mine.dueFollowUps.dueToday > 0 ? 'warn' : 'good'} onClick={() => { requestOpen('crm', 'due'); onGo('crm'); }} />
                <Stat n={mine.dueFollowUps.overdue} label="متابعة متأخرة"
                  tone={mine.dueFollowUps.overdue > 0 ? 'bad' : 'good'} onClick={() => { requestOpen('crm', 'overdue'); onGo('crm'); }} />
                {/* المحسوبة في التارجت لا كل التسجيلات (المراجعة الشاملة 02/10/2026) — فلا يظنّ أنه بلغ ثم يُخصم منه */}
                <Stat n={mine.today.counted ?? mine.today.contacts} label="متابعات اتحسبت في التارجت" />
                <Stat n={mine.today.convertedToBooking} label="حجز من متابعة" tone="good" />
              </Grid>
            </Card>
          )}

          {quick}

          {bank && (
            <Card title="📱 بنك أرقامك">
              <Grid>
                <Stat n={bank.available} label="رقم لسه في بنكك" onClick={() => onGo('crm')} />
                <Stat n={bank.remainingToday} label="تقدر تسحب النهاردة"
                  tone={bank.remainingToday > 0 ? 'good' : 'plain'}
                  hint={`من حدّ ${bank.limit} في اليوم`} onClick={() => onGo('crm')} />
              </Grid>
            </Card>
          )}
        </>
      )}

      {/* الرحلات القريبة — للجميع: البياع يتابع عميله، والأدمن يتابع الشركة */}
      {trips && (
        <Card title="✈️ رحلات قرّبت — خلال أسبوع"
          action={trips.length > 0 ? <Go onClick={() => onGo('upcoming')}>كل القادمة</Go> : undefined}>
          {trips.length === 0 ? <Empty>مفيش رحلات في الأسبوع الجاي.</Empty> : (
            <ul className="divide-y divide-slate-100">
              {trips.slice(0, 4).map(t => (
                <li key={t.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2">
                  <span className="min-w-0 font-medium text-ink-900">{t.customerName}</span>
                  <span className="text-xs text-ink-500">
                    {t.toCity ? `${t.toCity} · ` : ''}<span className="tabular-nums">{shortDate(t.departureDate)}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {trips.length > 4 && <p className="mt-2 text-xs text-ink-500">و{trips.length - 4} غيرها.</p>}
        </Card>
      )}

      {!admin && mineRow && target !== null && target > 0 && (
        <Card title="🎯 ماشي إزاي الشهر ده">
          <TargetBar done={mineRow.monthToDate.NEW} target={target} />
          <div className="mt-3">
            <Grid>
              <Stat n={mineRow.day.total} label="عملياتك النهاردة" />
              <Stat n={mineRow.monthToDate.total} label="كل عمليات شهرك" />
              <Stat n={mineRow.monthToDate.CANCELLATION} label="إلغاءات"
                tone={mineRow.monthToDate.CANCELLATION > 0 ? 'warn' : 'plain'} />
              <Stat n={mineRow.monthToDate.crm} label="متابعاتك" onClick={() => onGo('crm')} />
            </Grid>
          </div>
        </Card>
      )}

    </div>
  );
}
